import { useMemo, useState } from "react";
import { ACENTO } from "./cores";

const GRAOS = [
  { id: "dia", rotulo: "Dia" },
  { id: "mes", rotulo: "Mês" },
  { id: "ano", rotulo: "Ano" },
];

const MESES = ["jan", "fev", "mar", "abr", "mai", "jun",
               "jul", "ago", "set", "out", "nov", "dez"];

function agrupar(dias, grao) {
  if (grao === "dia") return dias.slice(-31).map((d) => ({
    chave: d.dia,
    rotulo: d.dia.slice(8, 10) + "/" + d.dia.slice(5, 7),
    valor: d.enviados,
  }));
  const mapa = new Map();
  for (const d of dias) {
    const chave = grao === "mes" ? d.dia.slice(0, 7) : d.dia.slice(0, 4);
    mapa.set(chave, (mapa.get(chave) || 0) + d.enviados);
  }
  return [...mapa.entries()].sort(([a], [b]) => a.localeCompare(b)).map(([chave, valor]) => ({
    chave,
    rotulo: grao === "mes"
      ? `${MESES[parseInt(chave.slice(5, 7), 10) - 1]}/${chave.slice(2, 4)}`
      : chave,
    valor,
  }));
}

/**
 * Linha de volume de envios com grao por dia, mes ou ano.
 *
 * A agregacao e' feita aqui em cima das linhas diarias que o backend ja manda —
 * trocar o grao nao gera requisicao nova.
 */
export default function GraficoLinha({ dias, titulo = "Volume de envios" }) {
  const [grao, setGrao] = useState("dia");
  const [ativo, setAtivo] = useState(null);

  const pontos = useMemo(() => agrupar(dias || [], grao), [dias, grao]);

  const L = 720, A = 180, PAD_X = 8, PAD_Y = 12;
  const max = Math.max(...pontos.map((p) => p.valor), 1);
  const passo = pontos.length > 1 ? (L - PAD_X * 2) / (pontos.length - 1) : 0;
  const x = (i) => PAD_X + i * passo;
  const y = (v) => A - PAD_Y - ((A - PAD_Y * 2) * v) / max;

  const linha = pontos.map((p, i) => `${x(i)},${y(p.valor)}`).join(" ");
  const area = pontos.length > 1
    ? `M ${x(0)},${A - PAD_Y} L ${linha.split(" ").join(" L ")} L ${x(pontos.length - 1)},${A - PAD_Y} Z`
    : "";

  // Rotulos do eixo X esparsos: no maximo ~6, senao viram serrilha ilegivel.
  const cadaQuantos = Math.max(1, Math.ceil(pontos.length / 6));

  return (
    <div className="rounded-xl border bg-card p-5">
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <h3 className="font-semibold">{titulo}</h3>
        <div className="flex gap-1 rounded-lg border p-1">
          {GRAOS.map((g) => (
            <button key={g.id} onClick={() => setGrao(g.id)}
              className={`rounded-md px-3 py-1 text-xs transition-colors ${
                grao === g.id ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`}>
              {g.rotulo}
            </button>
          ))}
        </div>
      </div>

      {!pontos.length ? (
        <p className="text-sm text-muted-foreground py-10 text-center">
          Sem envios ainda — o gráfico aparece a partir da primeira campanha.
        </p>
      ) : (
        <div className="relative">
          <svg viewBox={`0 0 ${L} ${A + 22}`} className="w-full" role="img"
            aria-label="Volume de envios ao longo do tempo">
            {[0.5, 1].map((f) => (
              <line key={f} x1={PAD_X} x2={L - PAD_X}
                y1={y(max * f)} y2={y(max * f)}
                className="stroke-border/60" strokeWidth="1" strokeDasharray="3 4" />
            ))}
            <text x={L - PAD_X} y={y(max) - 4} textAnchor="end"
              className="fill-current text-muted-foreground" fontSize="10">
              {max.toLocaleString("pt-BR")}
            </text>

            {area && <path d={area} fill={ACENTO} opacity="0.12" />}
            {pontos.length > 1 && (
              <polyline points={linha} fill="none" stroke={ACENTO}
                strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
            )}

            {pontos.map((p, i) => (
              <g key={p.chave}>
                {/* alvo de hover maior que o ponto */}
                <rect x={x(i) - passo / 2 || 0} y={0} width={passo || L} height={A}
                  fill="transparent"
                  onMouseEnter={() => setAtivo(i)} onMouseLeave={() => setAtivo(null)} />
                <circle cx={x(i)} cy={y(p.valor)} r={ativo === i ? 5 : pontos.length <= 31 ? 3 : 0}
                  fill={ACENTO} className="pointer-events-none"
                  style={{ transition: "r .1s" }} />
                {i % cadaQuantos === 0 && (
                  <text x={x(i)} y={A + 14} textAnchor="middle"
                    className="fill-current text-muted-foreground" fontSize="10">
                    {p.rotulo}
                  </text>
                )}
              </g>
            ))}
          </svg>

          {ativo !== null && pontos[ativo] && (
            <div className="absolute -top-1 left-1/2 -translate-x-1/2 rounded-md bg-foreground text-background px-2.5 py-1 text-xs tabular-nums pointer-events-none">
              {pontos[ativo].rotulo} · {pontos[ativo].valor.toLocaleString("pt-BR")} envios
            </div>
          )}
        </div>
      )}
    </div>
  );
}
